import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Banknote, Users, AlertCircle, Clock, Bus, Package,
  Trophy, Baby, Music, Sun, Phone
} from 'lucide-react';

const MODULE_LABELS = {
  Afterschool: 'Afterschool',
  Bus: 'Bus',
  BusRental: 'Bus Rental',
  Activities: 'Activities',
  Daycare: 'Daycare',
  ArtSchool: 'Art School',
  Colonie: 'Colonie',
  Ifada: 'Ifada',
  Registration: 'Registration'
};

const ENROLLMENT_ICONS = {
  Afterschool: Package,
  Bus: Bus,
  Activities: Trophy,
  Daycare: Baby,
  ArtSchool: Music,
  Colonie: Sun
};

const moduleLabel = (m) => MODULE_LABELS[m] || m;

const fmtUSD = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtLBP = (n) => (n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' LBP';
const fmtAmount = (n, currency) => (currency === 'LBP' ? fmtLBP(n) : fmtUSD(n));

const sumBy = (rows, currency) =>
  rows.filter(r => (r.Currency || 'USD') === currency).reduce((s, r) => s + (r.Total || 0), 0);

const Dashboard = ({ apiCall, selectedYear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const overview = await apiCall(`/dashboard/overview?academicYear=${selectedYear}`);
      setData(overview);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading dashboard...</div>;
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 flex items-center gap-3">
        <AlertCircle size={20} />
        <span>{error || 'No dashboard data'}</span>
      </div>
    );
  }

  const { cashToday, cashWeek, outstandingByYear, outstandingByModule, topFamilies, enrollments, pendingCharges } = data;

  const todayUSD = sumBy(cashToday, 'USD');
  const todayLBP = sumBy(cashToday, 'LBP');
  const weekUSD = sumBy(cashWeek, 'USD');
  const weekLBP = sumBy(cashWeek, 'LBP');
  const todayCount = cashToday.reduce((s, r) => s + (r.PaymentCount || 0), 0);

  const monthName = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* ============ TODAY'S CASH POSITION ============ */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Today's Cash Position</h2>
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
              <DollarSign size={16} /> Today (USD)
            </div>
            <p className="text-2xl font-bold text-green-800">{fmtUSD(todayUSD)}</p>
            <p className="text-xs text-green-600 mt-1">{todayCount} payment{todayCount === 1 ? '' : 's'} today</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-700 text-sm mb-1">
              <Banknote size={16} /> Today (LBP)
            </div>
            <p className="text-2xl font-bold text-amber-800">{fmtLBP(todayLBP)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 text-sm mb-1">
              <DollarSign size={16} /> Last 7 Days (USD)
            </div>
            <p className="text-2xl font-bold text-blue-800">{fmtUSD(weekUSD)}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-indigo-700 text-sm mb-1">
              <Banknote size={16} /> Last 7 Days (LBP)
            </div>
            <p className="text-2xl font-bold text-indigo-800">{fmtLBP(weekLBP)}</p>
          </div>
        </div>

        {cashToday.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b text-sm">
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Module</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Method</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Payments</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {cashToday.map((row, i) => (
                <tr key={i} className="border-b text-sm hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{moduleLabel(row.Module)}</td>
                  <td className="px-4 py-2">{row.PaymentMethod || '—'}</td>
                  <td className="px-4 py-2 text-right">{row.PaymentCount}</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtAmount(row.Total, row.Currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 italic text-sm text-center py-3">No payments recorded today</p>
        )}
      </div>

      {/* ============ ENROLLMENT SNAPSHOT ============ */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Active Enrollments <span className="text-sm font-normal text-gray-500">— {selectedYear}</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {enrollments.map(e => {
            const Icon = ENROLLMENT_ICONS[e.Module] || Users;
            return (
              <div key={e.Module} className="border border-gray-200 rounded-lg p-4 text-center">
                <Icon className="mx-auto text-blue-600 mb-2" size={22} />
                <p className="text-2xl font-bold text-gray-800">{e.ActiveCount}</p>
                <p className="text-xs text-gray-500">{moduleLabel(e.Module)}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ OUTSTANDING OVERVIEW ============ */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Outstanding by Year</h3>
          {outstandingByYear.length > 0 ? (
            <div className="space-y-2">
              {outstandingByYear.map(row => (
                <div key={row.AcademicYear} className="flex justify-between items-center border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-700">{row.AcademicYear}</span>
                  <span className={`font-bold ${row.Total > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtUSD(row.Total)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-1 text-sm">
                <span className="font-semibold text-gray-800">Total</span>
                <span className="font-bold text-red-700">
                  {fmtUSD(outstandingByYear.reduce((s, r) => s + (r.Total || 0), 0))}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 italic text-sm">Nothing outstanding</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">
            Outstanding by Module <span className="text-sm font-normal text-gray-500">— {selectedYear}</span>
          </h3>
          {outstandingByModule.length > 0 ? (
            <div className="space-y-2">
              {outstandingByModule.map(row => (
                <div key={row.Module} className="flex justify-between items-center border-b border-gray-100 pb-2 text-sm">
                  <span className="text-gray-700">{moduleLabel(row.Module)}</span>
                  <span className={`font-bold ${row.Total > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtUSD(row.Total)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic text-sm">Nothing outstanding for {selectedYear}</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Top Outstanding Families</h3>
          {topFamilies.length > 0 ? (
            <div className="space-y-2">
              {topFamilies.map(f => (
                <div key={f.FamilyID} className="flex justify-between items-center border-b border-gray-100 pb-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{f.FamilyID} — {f.FatherName}</p>
                    {f.PrimaryPhone && (
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{f.PrimaryPhone}</p>
                    )}
                  </div>
                  <span className="font-bold text-red-600 whitespace-nowrap pl-2">{fmtUSD(f.Total)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic text-sm">No families with outstanding balance</p>
          )}
        </div>
      </div>

      {/* ============ PENDING CHARGES ============ */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          <span className="inline-flex items-center gap-2"><Clock size={20} className="text-orange-500" />
            Pending Charges <span className="text-sm font-normal text-gray-500">— {monthName}</span>
          </span>
        </h2>
        {pendingCharges.length > 0 ? (
          <table className="w-full max-w-2xl">
            <thead>
              <tr className="bg-gray-100 border-b text-sm">
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Module</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Pending Charges</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pendingCharges.map(row => (
                <tr key={row.Module} className="border-b text-sm hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{moduleLabel(row.Module)}</td>
                  <td className="px-4 py-2 text-right">{row.PendingCount}</td>
                  <td className="px-4 py-2 text-right font-semibold text-orange-700">{fmtUSD(row.TotalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 italic text-sm">No pending charges this month</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
